import { StandardEvents } from '@shopify/events';
import Glide from '@theme/glide-esm';
import { sliderRegistry } from '@theme/slider-registry';

/** Glide expects pixels; custom properties retain their authored length units. */
export function sliderGapInPixels(value, rootFontSize, elementFontSize) {
  const match = value.trim().match(/^(-?\d*\.?\d+)(rem|em|px)?$/);
  if (!match) return 0;
  const amount = Number(match[1]);
  const scale = match[2] === 'rem' ? rootFontSize : match[2] === 'em' ? elementFontSize : 1;
  const pixels = amount * scale;
  return Number.isFinite(pixels) ? Math.max(0, pixels) : 0;
}

/** Preserve the viewport unless the selected slide falls outside it. */
export function sliderSelectionIndex(index, selected, count, perView) {
  const max = Math.max(0, count - perView);
  let next = Math.min(Math.max(0, index), max);
  if (selected >= 0) {
    if (selected < next) next = selected;
    else if (selected >= next + perView) next = selected - perView + 1;
  }
  return Math.min(Math.max(0, next), max);
}

/** Owns Glide's lifecycle, including section hydration and variant morphs. */
export class ThemeSlider extends HTMLElement {
  static observedAttributes = ['preset'];
  #glide;
  #observer;
  #resizeObserver;
  #controller;
  #frame;
  #generation = 0;
  #index = 0;
  #geometry = '';
  #width = 0;

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#observer = new MutationObserver((records) => {
      // Hydration can retain each slide and replace only its placeholder child.
      // Observe slide contents too, but not Glide styles or nested card previews.
      if (records.some(({ target }) => target === this ||
        target.matches?.('.glide__track, .glide__slides, .glide__slide'))) this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(this);
    window.addEventListener('resize', this.#resize, { signal });
    if (this.dataset.morphing === 'product-select') {
      this.closest('variant-picker')?.addEventListener(StandardEvents.productSelect, this.#productSelect, { signal });
    }
    this.#schedule();
  }

  disconnectedCallback() {
    this.#generation++;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#destroy();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  showSlide(index) {
    this.#destroy();
    this.#index = Math.max(0, Number(index) || 0);
    if (this.isConnected) this.#schedule();
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (this.isConnected) this.#mount();
    });
  };

  #slides() {
    return this.querySelector('[data-glide-el="track"] > .glide__slides');
  }

  #options() {
    const css = getComputedStyle(this);
    return {
      perView: Math.max(1, Number.parseFloat(css.getPropertyValue('--glide-per-view')) || 1),
      gap: sliderGapInPixels(
        css.getPropertyValue('--glide-gap'),
        Number.parseFloat(getComputedStyle(document.documentElement).fontSize),
        Number.parseFloat(css.fontSize)
      ),
    };
  }

  #visibleIndex(index, perView) {
    const slides = Array.from(this.#slides()?.children || []);
    const selected = this.getAttribute('preset') === 'variantSwatches'
      ? slides.findIndex((slide) => slide.querySelector('input:checked')) : -1;
    return sliderSelectionIndex(index, selected, slides.length, perView);
  }

  #mount() {
    this.#destroy();
    const track = this.querySelector('[data-glide-el="track"]');
    if (track) track.scrollLeft = 0;
    const preset = sliderRegistry[this.getAttribute('preset')];
    const count = this.#slides()?.children.length || 0;
    if (!preset || !count || !this.clientWidth) return;
    const options = this.#options();
    this.#geometry = JSON.stringify(options);
    this.#width = this.clientWidth;
    this.#index = this.#visibleIndex(this.#index, options.perView);
    this.#glide = new Glide(this, {
      ...preset,
      ...options,
      type: 'slider', // No clones: safe for Shopify DOM morphing.
      bound: true,
      keyboard: false, // Multiple sliders must not respond to document arrow keys together.
      startAt: this.#index,
      animationDuration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 450,
    });
    this.#glide.on(['mount.after', 'run.after', 'update', 'resize'], this.#syncControls);
    // CSS owns breakpoints. Glide's built-in module would restore its original
    // counts on every resize, overwriting the values read from CSS.
    this.#glide.mount({ Breakpoints: () => ({}) });
    this.#syncControls();
  }

  #destroy() {
    if (this.#glide) {
      this.#index = this.#glide.index;
      this.#glide.destroy();
      this.#glide = undefined;
    }
    this.removeAttribute('data-slider-ready');
  }

  #resize = () => {
    if (!this.isConnected) return;
    if (!this.#glide) {
      this.#schedule();
      return;
    }
    const options = this.#options();
    const geometry = JSON.stringify(options);
    if (geometry === this.#geometry && this.clientWidth === this.#width) return;
    this.#geometry = geometry;
    this.#width = this.clientWidth;
    this.#glide.update({ ...options, startAt: this.#visibleIndex(this.#glide.index, options.perView) });
    this.#syncControls();
  };

  #syncControls = () => {
    if (!this.#glide) return;
    const max = Math.max(0, (this.#slides()?.children.length || 0) - this.#glide.settings.perView);
    for (const button of this.querySelectorAll('.theme-slider__arrow')) {
      const previous = button.dataset.glideDir === '<';
      const disabled = max === 0 || (!this.#glide.settings.rewind &&
        (previous ? this.#glide.index <= 0 : this.#glide.index >= max));
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    }
    this.setAttribute('data-slider-ready', '');
  };

  #productSelect = (event) => {
    const generation = ++this.#generation;
    cancelAnimationFrame(this.#frame);
    this.#destroy(); // Give the morph clean server-shaped markup before fetching.
    // Pre-init CSS preserves cell sizes; retain the viewport while the request
    // is pending without leaving Glide's inline transforms for the morph.
    const track = this.querySelector('[data-glide-el="track"]');
    const options = this.#options();
    if (track) track.scrollLeft = this.#index * (track.clientWidth + options.gap) / options.perView;
    const finish = () => {
      if (this.isConnected && generation === this.#generation) this.#schedule();
    };
    Promise.resolve(event.promise).then(finish, finish);
  };
}

if (!customElements.get('theme-slider')) customElements.define('theme-slider', ThemeSlider);
